import { useEffect, useMemo } from "react";
import { type AnyZodObject, z } from "zod";
import { useCustomFormContext } from "@/components/site-builder/modules/CustomFormModule/context/customFormContext";
import { useCustomFormFieldProps } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormFieldProps";
import { useCustomFormStateOfResidenceOptions } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormStateOfResidenceOptions";
import { createFieldValidation } from "@/components/site-builder/modules/CustomFormModule/utils/createFieldValidation";
import { getFormFields } from "@/components/site-builder/modules/CustomFormModule/utils/getFormFields";
import { FormBuilderFieldCode } from "@/constants/form-builder";
import { useTranslation } from "@/i18n/client";
import type { FormBuilderFormField } from "@/types/form-builder";
import type { UseFormReturn } from "react-hook-form";

export interface UseCustomFormSetStateOfResidenceValidationProps {
  form: UseFormReturn;
  setStateOfResidenceValidationSchema: (value: AnyZodObject | null) => void;
}

export function useCustomFormSetStateOfResidenceValidation(
  props: UseCustomFormSetStateOfResidenceValidationProps,
) {
  const {
    form: { control, setValue },
    setStateOfResidenceValidationSchema,
  } = props;

  const { t } = useTranslation("form-elements");
  const { formEntity } = useCustomFormContext();
  const stateField = useMemo(() => {
    return getFormFields(formEntity).find(
      (field) => field.code === FormBuilderFieldCode.StateOfResidence,
    );
  }, [formEntity]);
  const { fieldName: stateFieldName } = useCustomFormFieldProps(
    stateField as FormBuilderFormField,
  );
  const stateOptions = useCustomFormStateOfResidenceOptions(control);
  const hasStateField = stateOptions.length > 0;

  useEffect(() => {
    if (stateField) {
      if (hasStateField) {
        const stateValidation = {
          [stateFieldName]: createFieldValidation(
            { ...stateField, required: true },
            t,
          ),
        };
        setStateOfResidenceValidationSchema(z.object(stateValidation));
      } else {
        setStateOfResidenceValidationSchema(null);
        setValue(stateFieldName, "");
      }
    }
  }, [
    hasStateField,
    stateFieldName,
    setValue,
    setStateOfResidenceValidationSchema,
    t,
    stateField,
  ]);
}
